import axios from 'axios';
import logger from './logger';

// Shared Brewfather fetch/sync machinery used by BatchContext and RecipeContext.
//
// The Brewfather API is rate limited per user, and its `order_by=_timestamp_ms`
// sorting is unreliable, so incremental syncs compare fetched items against the
// local cache and stop at the first unchanged item instead of trusting the sort.

export const BREWFATHER_API_BASE = 'https://api.brewfather.app/v2';

const PAGE_SIZE = 50; // Brewfather API max page size

export interface SyncItem {
  _id: string;
  _timestamp_ms?: number;
  _lastFetched?: number;
}

interface RequestOptions {
  endpoint: string;
  apiKey: string;
  params?: Record<string, unknown>;
  retryDelayMs?: number;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// GET with retry on 429 (honoring Retry-After when present) and transient errors.
const getWithRetry = async (
  endpoint: string,
  apiKey: string,
  params: Record<string, unknown>,
  retryDelayMs: number
): Promise<any[]> => {
  const maxRetries = 2;
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await axios.get(`${BREWFATHER_API_BASE}/${endpoint}`, {
        params,
        headers: { Authorization: `Basic ${apiKey}` },
      });
      return response.data;
    } catch (error: any) {
      lastError = error;
      const status = error?.response?.status;
      // Only retry rate limits and transient server errors
      if (attempt < maxRetries && (status === 429 || status === undefined || status >= 500)) {
        const retryAfter = Number(error?.response?.headers?.['retry-after']);
        const delay = !isNaN(retryAfter) && retryAfter > 0
          ? retryAfter * 1000
          : retryDelayMs * Math.pow(2, attempt);
        logger.warn(`Brewfather ${endpoint} request failed (status ${status}), retrying in ${delay}ms`);
        await sleep(delay);
        continue;
      }
      throw error;
    }
  }
  throw lastError;
};

const stamp = <T extends SyncItem>(items: any[]): T[] => {
  const now = Date.now();
  return items.map((item) => ({ ...item, _lastFetched: now }));
};

/**
 * Fetch every item via start_after pagination.
 * Throws on failure — a partial result must never replace a complete cache.
 */
export const fetchAllPaginated = async <T extends SyncItem>({
  endpoint,
  apiKey,
  params = {},
  retryDelayMs = 2000,
}: RequestOptions): Promise<T[]> => {
  const all: T[] = [];
  let lastId = '';

  while (true) {
    const pageParams: Record<string, unknown> = { ...params, limit: PAGE_SIZE };
    if (lastId) pageParams.start_after = lastId;

    const page = stamp<T>(await getWithRetry(endpoint, apiKey, pageParams, retryDelayMs));
    all.push(...page);

    if (page.length < PAGE_SIZE) break;
    lastId = page[page.length - 1]._id;
  }

  logger.log(`Full ${endpoint} fetch: ${all.length} items`);
  return all;
};

export interface IncrementalResult<T> {
  status: 'upToDate' | 'changes' | 'needFull';
  items: T[];
}

/**
 * Fetch the `limit` most recently modified items and diff them against the
 * cache, stopping at the first unchanged item (workaround for Brewfather's
 * unreliable last-modified ordering).
 *
 * - upToDate: nothing changed since the cache was written
 * - changes:  the returned items are the complete set of changes
 * - needFull: the change set may exceed `limit`; caller must escalate
 */
export const fetchIncremental = async <T extends SyncItem>({
  endpoint,
  apiKey,
  limit,
  params = {},
  retryDelayMs = 2000,
  cached,
}: RequestOptions & { limit: number; cached: T[] | null }): Promise<IncrementalResult<T>> => {
  const fetched = stamp<T>(
    await getWithRetry(
      endpoint,
      apiKey,
      { ...params, limit, order_by: '_timestamp_ms', order_by_direction: 'desc' },
      retryDelayMs
    )
  );

  if (!cached || cached.length === 0) {
    return { status: 'needFull', items: [] };
  }

  const cachedById = new Map(cached.map((item) => [item._id, item]));
  const changed: T[] = [];
  let foundUnchanged = false;

  for (const item of fetched) {
    const cachedItem = cachedById.get(item._id);
    if (cachedItem && cachedItem._timestamp_ms === item._timestamp_ms) {
      foundUnchanged = true;
      break;
    }
    changed.push(item);
  }

  if (foundUnchanged) {
    return { status: changed.length === 0 ? 'upToDate' : 'changes', items: changed };
  }

  // No unchanged item found. If we got fewer items than requested we saw the
  // whole dataset, so the change set is still complete. Otherwise escalate.
  if (fetched.length < limit) {
    return { status: 'changes', items: changed };
  }
  return { status: 'needFull', items: [] };
};

/** Merge updates into an existing list by _id, newest first. */
export const mergeById = <T extends SyncItem>(existing: T[], updates: T[]): T[] => {
  const merged = [...existing];
  for (const update of updates) {
    const index = merged.findIndex((item) => item._id === update._id);
    if (index >= 0) {
      merged[index] = update;
    } else {
      merged.push(update);
    }
  }
  merged.sort((a, b) => (b._timestamp_ms || 0) - (a._timestamp_ms || 0));
  return merged;
};

export type SyncOutcome<T> =
  | { type: 'noChanges' }
  | { type: 'merged'; items: T[] }
  | { type: 'full'; items: T[] };

/**
 * Rate-limit-friendly sync: probe 10, escalate to 50, fall back to a full
 * paginated fetch only when the change set genuinely exceeds both windows.
 * Throws on failure; callers keep their existing data.
 */
export const syncResource = async <T extends SyncItem>({
  endpoint,
  apiKey,
  params = {},
  retryDelayMs = 2000,
  getCached,
}: RequestOptions & { getCached: () => T[] | null }): Promise<SyncOutcome<T>> => {
  const cached = getCached();

  if (cached && cached.length > 0) {
    for (const limit of [10, 50]) {
      const result = await fetchIncremental<T>({ endpoint, apiKey, params, retryDelayMs, limit, cached });
      if (result.status === 'upToDate') return { type: 'noChanges' };
      if (result.status === 'changes') return { type: 'merged', items: result.items };
    }
  }

  const items = await fetchAllPaginated<T>({ endpoint, apiKey, params, retryDelayMs });
  items.sort((a, b) => (b._timestamp_ms || 0) - (a._timestamp_ms || 0));
  return { type: 'full', items };
};
